import { createContext, useContext, type ReactNode } from "react";
import type { ArtifactPreview } from "~/services/artifacts";

export interface ArtifactLineageAction {
  isOpen: boolean;
  open: () => void;
}

export interface ArtifactShareAction {
  isShared: boolean;
  // False until whether it is shared is known, and while a change is saving.
  isReady: boolean;
  toggle: () => void;
}

/** What the surface around an artifact card lets it do. A null action is one
 *  this surface does not offer. */
export interface ArtifactCardActions {
  // Replaces the artifact's own name as the card title, e.g. a pin's name.
  title?: string;
  // Opens the lineage side panel. Without one, the card shows lineage inline.
  lineage: ArtifactLineageAction | null;
  share: ArtifactShareAction | null;
}

type Resolve = (preview: ArtifactPreview) => ArtifactCardActions;

// A resolver rather than a value because one surface holds many cards: a
// session resolves each card against the project's pins.
const ArtifactCardActionsContext = createContext<Resolve | null>(null);

export const ArtifactCardActionsProvider = ({
  resolve,
  children,
}: {
  // Null offers no actions, so cards fall back to their inline views.
  resolve: Resolve | null;
  children: ReactNode;
}) => (
  <ArtifactCardActionsContext.Provider value={resolve}>
    {children}
  </ArtifactCardActionsContext.Provider>
);

/** The card's actions, or null on a surface with no side panel or sharing,
 *  such as a share-link or admin view. */
export const useArtifactCardActions = (preview: ArtifactPreview): ArtifactCardActions | null =>
  useContext(ArtifactCardActionsContext)?.(preview) ?? null;
